import { lazy, Suspense } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { connectWhep, disconnectWhep } from '@/utils/whep'

const HlsPlayer = lazy(() => import('@/components/HlsPlayer'))

const streamFrameSx = {
  position: 'relative',
  width: '100%',
  minWidth: { xs: 280, sm: 480 },
  maxWidth: 720,
  aspectRatio: '16 / 9',
  bgcolor: '#000',
}

/**
 * HLS first: direct MediaMTX HLS URL works in browser; WHEP often fails (ICE/NAT).
 * HLS via hls.js needs CSP media-src to allow blob: (see nginx.conf).
 */
export function LiveStreamPlayer({ webrtc, hls, onExhausted }) {
  const [mode, setMode] = useState(() => pickMode(webrtc, hls))
  const exhaustedRef = useRef(false)
  const triedRef = useRef(new Set())

  useEffect(() => {
    exhaustedRef.current = false
    triedRef.current = new Set()
    setMode(pickMode(webrtc, hls))
  }, [webrtc, hls])

  const failExhausted = useCallback(() => {
    setMode('error')
    if (exhaustedRef.current) return
    exhaustedRef.current = true
    onExhausted?.()
  }, [onExhausted])

  const fallbackFromHls = useCallback(() => {
    triedRef.current.add('hls')
    if (webrtc && !triedRef.current.has('webrtc')) {
      setMode('webrtc')
      return
    }
    failExhausted()
  }, [webrtc, failExhausted])

  const fallbackFromWebRtc = useCallback(() => {
    triedRef.current.add('webrtc')
    if (hls && !triedRef.current.has('hls')) {
      setMode('hls')
      return
    }
    failExhausted()
  }, [hls, failExhausted])

  if (mode === 'error') {
    return <Alert severity="error">No stream available</Alert>
  }

  if (mode === 'webrtc') {
    if (!webrtc) {
      return <Alert severity="error">No stream available</Alert>
    }
    return <WhepPlayer src={webrtc} onFatalError={fallbackFromWebRtc} />
  }

  if (!hls) {
    return <Alert severity="error">No stream available</Alert>
  }
  return (
    <Box sx={streamFrameSx}>
      <Suspense fallback={<LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}>
        <HlsPlayer src={hls} fill onFatalError={fallbackFromHls} />
      </Suspense>
    </Box>
  )
}

function pickMode(webrtc, hls) {
  if (hls) return 'hls'
  if (webrtc) return 'webrtc'
  return 'error'
}

function WhepPlayer({ src, onFatalError }) {
  const videoRef = useRef(null)
  const sessionRef = useRef(null)
  const onFatalErrorRef = useRef(onFatalError)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(true)

  onFatalErrorRef.current = onFatalError

  useEffect(() => {
    let cancelled = false
    const video = videoRef.current
    if (!video || !src) return undefined

    setLoading(true)

    ;(async () => {
      try {
        const session = await connectWhep(video, src)
        if (cancelled) {
          await disconnectWhep(session.pc, session.resourceUrl)
          return
        }
        sessionRef.current = session

        if (video.videoWidth > 0) setLoading(false)

        session.whenReady.catch(() => {
          if (cancelled) return
          onFatalErrorRef.current?.()
        })
      } catch {
        if (cancelled) return
        setLoading(false)
        onFatalErrorRef.current?.()
      }
    })()

    return () => {
      cancelled = true
      const session = sessionRef.current
      sessionRef.current = null
      if (video) video.srcObject = null
      if (session) disconnectWhep(session.pc, session.resourceUrl)
    }
  }, [src])

  return (
    <Box sx={streamFrameSx}>
      {loading && (
        <CircularProgress
          size={25}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            translate: '-50% -50%',
            zIndex: 1000,
          }}
        />
      )}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedData={() => setLoading(false)}
        onWaiting={() => setLoading(true)}
        onPlaying={() => setLoading(false)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 0.25,
          px: 0.5,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
        }}
      >
        <IconButton
          onClick={() => {
            const v = videoRef.current
            if (!v) return
            playing ? v.pause() : v.play()
          }}
        >
          {playing ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
      </Box>
    </Box>
  )
}
